import React, { FunctionComponent, memo } from 'react';
import { Checkbox, Divider, InputField } from 'plume-ui';

import {
  getGenericTriggerTypeLabels,
  isGenericTriggerTypeForSupportOutreach,
} from './config';
import SupportGenericTriggerFooter from './SupportGenericTriggerFooter';
import { useTranslation } from 'react-i18next';
import MaxFrequencyFields from './MaxFrequencyFields';
import {
  OutreachTriggerTypes,
  SupportOutreachTriggerFormFields,
  SupportTriggerRendererProps,
} from '../../../../../types';
import { FormikSupportOutreachTriggerValue } from '../support/types';
import useFormControls from '../../../hooks/useFormControls';
import FormattedMessage from '../../../../../../../utils/components/FormattedMessage';
import OutreachConfigFieldMessages from '../../../../../../Outreach/components/OutreachConfigFieldMessages';
import { OutreachTypes } from '../../../../../../../urls';

export type SupportGenericTriggerDropdownProperties = SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit;

const mapTriggerTypeToInputValueLabels: Record<
  string,
  {
    triggered: string;
    cleared: string;
  }
> = {
  [OutreachTriggerTypes.MaxValue]: {
    triggered: 'number',
    cleared: 'number',
  },
  [OutreachTriggerTypes.CSPOutageDetected]: {
    triggered: 'number',
    cleared: 'percentage',
  },
  [OutreachTriggerTypes.MaxPercentage]: {
    triggered: 'percentage',
    cleared: 'percentage',
  },
  [OutreachTriggerTypes.MaxIncreaseRate]: {
    triggered: 'percentage',
    cleared: 'percentage',
  },
};

const SupportGenericTrigger: FunctionComponent<SupportTriggerRendererProps<
  FormikSupportOutreachTriggerValue,
  SupportGenericTriggerDropdownProperties
>> = ({ field, form, renderDropdownFor, triggerType, name, outreachType }) => {
  const {
    setPropertyValue,
    toggleFieldValue,
    getPropertyValue,
  } = useFormControls<FormikSupportOutreachTriggerValue>(field, form);
  const { t } = useTranslation();
  if (!isGenericTriggerTypeForSupportOutreach(triggerType)) {
    return null;
  }
  const labels = getGenericTriggerTypeLabels(triggerType);

  const inputValueLabel = mapTriggerTypeToInputValueLabels[triggerType] || {
    cleared: '',
    triggered: '',
  };

  const getClearMessageCheckboxLabelId = () => {
    if (outreachType === OutreachTypes.Email) {
      return 'outreach.form.supportOutreachTriggerSection.sendClearNotifications';
    }
    return 'outreach.form.subscriberOutreachTriggerSection.sendClearNotifications';
  };

  return (
    <>
      <div className="OutreachContainer__subscriber-outreach-trigger-label">
        <span className="color-required">* </span>
        <FormattedMessage id="outreach.form.supportOutreachTriggerSection.issueTriggered" />
      </div>
      <div className="OutreachContainer__subscriber-outreach-trigger-field">
        <div className="OutreachContainer__subscriber-outreach-trigger-field-wrapper">
          <FormattedMessage id={labels.issueTriggeredWhenLabel} />
          <InputField
            name={SupportOutreachTriggerFormFields.IssueTriggeredWhen}
            label={t(inputValueLabel.triggered)}
            placeholder="0"
            type="number"
            step="1"
            min={0}
            classes={(current) => ({
              ...current,
              root: `${current.root} m-x-s`,
            })}
            value={
              field.value[SupportOutreachTriggerFormFields.IssueTriggeredWhen]
            }
            onChange={(e) =>
              setPropertyValue(
                SupportOutreachTriggerFormFields.IssueTriggeredWhen,
                parseFloat(e.target.value) < 0 ? 0 : parseFloat(e.target.value),
              )
            }
          />
          <FormattedMessage id={labels.issueTriggeredWhenSuffixLabel} />
        </div>
        <OutreachConfigFieldMessages
          propertyName={SupportOutreachTriggerFormFields.IssueTriggeredWhen}
          field={field}
          form={form}
        />
      </div>
      <div className="OutreachContainer__subscriber-outreach-trigger-label">
        <span className="color-required">* </span>
        <FormattedMessage id="outreach.form.supportOutreachTriggerSection.issueCleared" />
      </div>
      <div className="OutreachContainer__subscriber-outreach-trigger-field">
        <div className="OutreachContainer__subscriber-outreach-trigger-field-wrapper">
          <FormattedMessage id={labels.issueClearedWhenLabel} />
          <InputField
            label={t(inputValueLabel.cleared)}
            placeholder="1"
            name={SupportOutreachTriggerFormFields.IssueClearedWhen}
            type="number"
            step={1}
            min={1}
            classes={(current) => ({
              ...current,
              root: `${current.root} m-x-s`,
            })}
            value={
              field.value[SupportOutreachTriggerFormFields.IssueClearedWhen]
            }
            onChange={(e) => {
              setPropertyValue(
                SupportOutreachTriggerFormFields.IssueClearedWhen,
                parseFloat(e.target.value) < 1 ? 1 : parseFloat(e.target.value),
              );
            }}
          />
          <FormattedMessage id={labels.issueClearedWhenSuffixLabel} />
        </div>
        <OutreachConfigFieldMessages
          propertyName={SupportOutreachTriggerFormFields.IssueClearedWhen}
          field={field}
          form={form}
        />
      </div>

      <MaxFrequencyFields
        renderDropdownFor={renderDropdownFor}
        field={field}
        form={form}
        outreachType={outreachType}
      />
      <div className="OutreachContainer__subscriber-outreach-trigger-field OutreachContainer__subscriber-outreach-trigger-field--schedule">
        <Checkbox
          onChange={() =>
            toggleFieldValue(
              SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared,
            )
          }
          checked={
            getPropertyValue(
              SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared,
            ) as boolean
          }
          label={t(getClearMessageCheckboxLabelId())}
        />
      </div>
      <Divider />
      <SupportGenericTriggerFooter
        renderDropdownFor={renderDropdownFor}
        triggerType={triggerType}
        name={name}
        field={field}
        form={form}
        outreachType={outreachType}
      />
    </>
  );
};

export default memo(SupportGenericTrigger);
